import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  Plus, Trash2, RefreshCw, TrendingUp, TrendingDown, Users,
  BarChart3, AtSign, Search, Target, Pencil, Check, X,
} from "lucide-react";
import { Facebook, Instagram, Linkedin, Twitter } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const PLATFORM_ICONS: Record<string, React.ElementType> = {
  instagram: Instagram, twitter: Twitter, linkedin: Linkedin,
  facebook: Facebook, threads: AtSign,
};
const PLATFORM_COLORS: Record<string, string> = {
  instagram: "#e1306c", twitter: "#1da1f2", linkedin: "#0077b5",
  facebook: "#1877f2", threads: "#101010",
};

function formatK(n: number) {
  if (!n) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export function CompetitorBenchmarkView() {
  const { orgId } = useOrgPermissions();
  const qc = useQueryClient();
  const { toast } = useToast();

  const [adding, setAdding]     = useState(false);
  const [syncing, setSyncing]   = useState<string | null>(null);
  const [editId, setEditId]     = useState<string | null>(null);
  const [form, setForm]         = useState({ name: "", platform: "instagram", handle: "", keywords: "" });
  const [editForm, setEditForm] = useState({ name: "", platform: "instagram", handle: "", keywords: "" });

  const { data: competitors = [], refetch } = useQuery({
    queryKey: ["competitors", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("competitor_tracking")
        .select("*")
        .eq("org_id", orgId!)
        .order("created_at", { ascending: false });
      return (data ?? []) as any[];
    },
  });

  const { data: mentions = [] } = useQuery({
    queryKey: ["competitor-mentions", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("mentions")
        .select("id,source,text,sentiment,created_at,keyword")
        .eq("org_id", orgId!)
        .order("created_at", { ascending: false })
        .limit(50);
      return (data ?? []) as any[];
    },
  });

  const handleAdd = async () => {
    if (!form.name.trim() || !orgId) return;
    const { data: { user } } = await supabase.auth.getUser();
    const keywords = form.keywords.split(",").map(k => k.trim()).filter(Boolean);
    const { error } = await (supabase as any).from("competitor_tracking").insert({
      org_id: orgId,
      created_by: user?.id,
      competitor_name: form.name.trim(),
      platform: form.platform,
      handle: form.handle.trim().replace(/^@/, ""),
      keywords,
      is_active: true,
    });
    if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    setForm({ name: "", platform: "instagram", handle: "", keywords: "" });
    setAdding(false);
    refetch();
    toast({ title: "Competitor added" });
  };

  const handleDelete = async (id: string) => {
    await (supabase as any).from("competitor_tracking").delete().eq("id", id);
    refetch();
  };

  const handleSaveEdit = async (id: string) => {
    const keywords = editForm.keywords.split(",").map(k => k.trim()).filter(Boolean);
    await (supabase as any).from("competitor_tracking").update({
      competitor_name: editForm.name,
      platform: editForm.platform,
      handle: editForm.handle.replace(/^@/, ""),
      keywords,
    }).eq("id", id);
    setEditId(null);
    refetch();
  };

  const handleSync = async (c: any) => {
    setSyncing(c.id);
    try {
      // Scan mentions for this competitor
      await supabase.functions.invoke("scan-social-mentions", {
        body: { competitorId: c.id, keywords: c.keywords, orgId },
      });
      // Count mentions in last 30d
      const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString();
      const { count } = await (supabase as any)
        .from("mentions")
        .select("id", { count: "exact", head: true })
        .eq("org_id", orgId)
        .in("keyword", c.keywords ?? [])
        .gte("created_at", thirtyDaysAgo);
      await (supabase as any).from("competitor_tracking").update({
        mention_count: count ?? 0,
        last_synced_at: new Date().toISOString(),
      }).eq("id", c.id);
      refetch();
      toast({ title: "Competitor synced", description: `${count ?? 0} mentions found in the last 30 days.` });
    } catch (e: any) {
      toast({ title: "Sync failed", description: e.message, variant: "destructive" });
    } finally {
      setSyncing(null);
    }
  };

  // Group mentions by competitor keyword
  const mentionsByKeyword: Record<string, number> = {};
  for (const m of mentions) {
    const k = m.keyword ?? "";
    mentionsByKeyword[k] = (mentionsByKeyword[k] ?? 0) + 1;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" /> Competitor Benchmarking
          </h2>
          <p className="text-sm text-muted-foreground">Track competitors' social presence and mentions</p>
        </div>
        <Button size="sm" onClick={() => setAdding(true)}>
          <Plus className="w-4 h-4 mr-1.5" /> Add Competitor
        </Button>
      </div>

      {/* Add form */}
      {adding && (
        <Card className="p-5 border-primary/30 bg-primary/5">
          <h3 className="font-semibold text-sm mb-4">New Competitor</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input placeholder="Competitor name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            <Select value={form.platform} onValueChange={v => setForm(f => ({ ...f, platform: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["instagram","twitter","linkedin","facebook","threads"].map(p => (
                  <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input placeholder="@handle (optional)" value={form.handle} onChange={e => setForm(f => ({ ...f, handle: e.target.value }))} />
            <Input placeholder="Keywords to track (comma-separated)" value={form.keywords} onChange={e => setForm(f => ({ ...f, keywords: e.target.value }))} />
          </div>
          <div className="flex gap-2 mt-4">
            <Button size="sm" onClick={handleAdd} disabled={!form.name.trim()}>Add</Button>
            <Button size="sm" variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
          </div>
        </Card>
      )}

      {/* Competitor cards */}
      {competitors.length === 0 && !adding ? (
        <Card className="p-10 text-center">
          <Target className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30" />
          <p className="font-medium text-muted-foreground">No competitors tracked yet</p>
          <p className="text-sm text-muted-foreground mt-1">Add competitors to benchmark their social performance against yours.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {competitors.map((c: any) => {
            const Icon = PLATFORM_ICONS[c.platform] ?? AtSign;
            const color = PLATFORM_COLORS[c.platform] ?? "#666";
            const isEditing = editId === c.id;

            return (
              <Card key={c.id} className="p-5">
                {isEditing ? (
                  <div className="space-y-3">
                    <Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} placeholder="Name" />
                    <Select value={editForm.platform} onValueChange={v => setEditForm(f => ({ ...f, platform: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["instagram","twitter","linkedin","facebook","threads"].map(p => (
                          <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input value={editForm.handle} onChange={e => setEditForm(f => ({ ...f, handle: e.target.value }))} placeholder="@handle" />
                    <Input value={editForm.keywords} onChange={e => setEditForm(f => ({ ...f, keywords: e.target.value }))} placeholder="Keywords (comma-separated)" />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleSaveEdit(c.id)}><Check className="w-3.5 h-3.5 mr-1" />Save</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditId(null)}><X className="w-3.5 h-3.5 mr-1" />Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: color }}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-semibold">{c.competitor_name}</p>
                          {c.handle && <p className="text-xs text-muted-foreground">@{c.handle}</p>}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" className="w-7 h-7"
                          onClick={() => { setEditId(c.id); setEditForm({ name: c.competitor_name, platform: c.platform ?? "instagram", handle: c.handle ?? "", keywords: (c.keywords ?? []).join(", ") }); }}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="w-7 h-7" onClick={() => handleSync(c)} disabled={syncing === c.id}>
                          <RefreshCw className={`w-3.5 h-3.5 ${syncing === c.id ? "animate-spin" : ""}`} />
                        </Button>
                        <Button size="icon" variant="ghost" className="w-7 h-7 text-destructive hover:text-destructive" onClick={() => handleDelete(c.id)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div className="bg-muted/40 rounded-lg p-3 text-center">
                        <p className="text-lg font-bold">{formatK(c.followers_count)}</p>
                        <p className="text-[10px] text-muted-foreground">Followers</p>
                      </div>
                      <div className="bg-muted/40 rounded-lg p-3 text-center">
                        <p className="text-lg font-bold">{c.mention_count ?? 0}</p>
                        <p className="text-[10px] text-muted-foreground">Mentions/30d</p>
                      </div>
                      <div className="bg-muted/40 rounded-lg p-3 text-center">
                        <p className="text-lg font-bold">{c.engagement_rate ? `${Number(c.engagement_rate).toFixed(1)}%` : "—"}</p>
                        <p className="text-[10px] text-muted-foreground">Engagement</p>
                      </div>
                    </div>

                    {(c.keywords ?? []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {c.keywords.map((kw: string) => (
                          <Badge key={kw} variant="secondary" className="text-[10px]">
                            <Search className="w-2.5 h-2.5 mr-0.5" />{kw}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {c.last_synced_at && (
                      <p className="text-[10px] text-muted-foreground">
                        Synced {formatDistanceToNow(new Date(c.last_synced_at), { addSuffix: true })}
                      </p>
                    )}
                  </>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Recent mentions feed */}
      {mentions.length > 0 && (
        <Card className="p-5">
          <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-primary" /> Recent Mentions
          </h3>
          <div className="space-y-2">
            {mentions.slice(0, 15).map((m: any) => (
              <div key={m.id} className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/30 transition-colors">
                <Badge variant={m.sentiment === "positive" ? "default" : m.sentiment === "negative" ? "destructive" : "secondary"}
                  className="text-[10px] shrink-0 mt-0.5 capitalize">{m.sentiment ?? "neutral"}</Badge>
                <div className="flex-1 min-w-0">
                  <p className="text-sm line-clamp-2">{m.text}</p>
                  <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
                    <span>{m.source}</span>
                    {m.keyword && <span>· <strong>{m.keyword}</strong></span>}
                    <span className="ml-auto">{formatDistanceToNow(new Date(m.created_at), { addSuffix: true })}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
